import React from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";
import { useDispatch } from "react-redux";

import { IMelodyTableData, ITaskEditorProps } from "../../../../../../common/types";
import { setItem, hasItem } from "../../../../../cookie";
import Icon from "../../../../../icon";
import { ICheckboxEvent } from "../../../../../types";
import { editTask } from "../../../../../store/plan/actions";
import { post } from "../../../../../actions/general";

import ScoreEditor from "../../../../Score";
import { Range, Checkbox } from "../../../../Form";

import EditorBar, {
    FlexLeft,
    FlexRight,
    Center,
    Changer,
    ChangerPopup,
} from "../../../EditorBar";

import getScore from "../../Common/getScore";
import getTempo from "../../Common/getTempo";
import ChangeColor from "../../Common/ChangeColor";
import ChangeTempo from "../../Common/ChangeTempo";
import useExerciseEditor from "../../Common/useExerciseEditor";

import messages from "./messages";

const MelodyTable = ({ taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen }: ITaskEditorProps) => {
    const dispatch = useDispatch();
    const {
        openChanger,
        playable,
        handleChangeOpen,
        setPopupRef,
        storeScore,
        changeTempo,
        setPlayable,
        exportScore,
        score,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });
    const changeSize = (val: number, name?: string) => {
        const task = plan.tasks[taskIndex];
        const data = task.data as IMelodyTableData;
        let size = {
            y: data.size ? data.size.y : 100,
            x: data.size ? data.size.x : 100,
        };
        size[name! as "x" | "y"] = val;
        dispatch(editTask({ taskIndex, changingProp: "size", val: size }));
    };

    const changeSizeStop = () => {
        const { size } = plan.tasks[taskIndex].data as IMelodyTableData;
        dispatch(post("melody-table-size", {
            planId: plan._id,
            index: taskIndex,
            size,
        }));
    };

    const changePosition = (val: number) => {
        dispatch(editTask({ taskIndex, changingProp: "position", val }));
    };

    const changePositionStop = () => {
        const { position } = plan.tasks[taskIndex].data as IMelodyTableData;
        dispatch(post("melody-table-position", {
            planId: plan._id,
            index: taskIndex,
            position: position,
        }));
    };

    const changeHint = (e: ICheckboxEvent) => {
        let hint = e.target.checked;
        dispatch(editTask({ taskIndex, changingProp: "hint", val: hint }));
        dispatch(post("rhyt-name-hint", {
            planId: plan._id,
            index: taskIndex,
            hint,
        }));
    };

    const task = plan.tasks[taskIndex];
    const data = task.data as IMelodyTableData;
    const { position } = data;
    let size = data.size || { x: 100, y: 100 };
    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    }
    return (
        <div>
            {inlineEditorOpen && (
                <ScoreEditor
                    taskType="melodyTable"
                    score={JSON.parse(JSON.stringify(getScore(plan, task) || {}))}
                    tempo={getTempo(plan, task)}
                    setScore={storeScore}
                    changeTempo={changeTempo}
                />
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft>
                        <Changer active={openChanger === "sizeX"}>
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                    handleChangeOpen("sizeX");
                                }}
                            >
                                <Icon i="width" />
                            </div>
                            {openChanger === "sizeX" && (
                                <ChangerPopup setRef={setPopupRef}>
                                    <Range
                                        scale={1}
                                        value={size.x || 100}
                                        onChange={changeSize}
                                        onStop={changeSizeStop}
                                        min={30}
                                        max={200}
                                        step={1}
                                        label={<FormattedMessage {...messages.width} />}
                                        name="x"
                                    />
                                </ChangerPopup>
                            )}
                        </Changer>
                        <Changer active={openChanger === "sizeY"}>
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                    handleChangeOpen("sizeY");
                                }}
                            >
                                <Icon i="height" />
                            </div>
                            {openChanger === "sizeY" && (
                                <ChangerPopup setRef={setPopupRef}>
                                    <Range
                                        scale={1}
                                        value={size.y || 100}
                                        onChange={changeSize}
                                        onStop={changeSizeStop}
                                        min={30}
                                        max={200}
                                        step={1}
                                        label={<FormattedMessage {...messages.height} />}
                                        name="y"
                                    />
                                </ChangerPopup>
                            )}
                        </Changer>
                        <Changer active={openChanger === "position"}>
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                    handleChangeOpen("position");
                                }}
                            >
                                <Icon i="position-down" />
                            </div>
                            {openChanger === "position" && (
                                <ChangerPopup setRef={setPopupRef}>
                                    <Range
                                        value={position || 0}
                                        onChange={changePosition}
                                        onStop={changePositionStop}
                                        min={-150}
                                        scale={1}
                                        max={200}
                                        step={2}
                                        label={<FormattedMessage {...messages.position} />}
                                        name="y"
                                    />
                                </ChangerPopup>
                            )}
                        </Changer>
                    </FlexLeft>
                    <Center
                        disabled={(inlineEditorOpen && !playable)}
                        tooltip={!playable && inlineEditorOpen && (
                            <FormattedMessage
                                {...(score.current?.keyChanges && score.current.keyChanges.length > 0
                                    ? messages.tonesetStaffNoKeyChange
                                    : messages.createScoreTooltip
                                )}
                            />
                        )}
                        onClick={() => {
                            toggleInlineEditor(!inlineEditorOpen);
                            setPlayable(true);
                            if (inlineEditorOpen) {
                                exportScore();
                            }
                            setItem("melodyTableDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn({
                            pulsate: !(inlineEditorOpen && !playable) &&
                                !hasItem("melodyTableDone") &&
                                !task.ref
                        })}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.editScore} />
                        )}
                    </Center>
                    <FlexRight>
                        <ChangeTempo {...forwardProps} />
                        <Changer>
                            <Checkbox
                                dark
                                label={<FormattedMessage {...messages.firstKnown} />}
                                checked={data.hint === undefined ? true : !!data.hint}
                                onChange={changeHint}
                            ></Checkbox>
                        </Changer>
                        <ChangeColor
                            {...forwardProps}
                            color={data?.color}
                        />
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
}

export default MelodyTable;
